import { useState, useEffect, useRef } from 'react';
import { register, validateRegisterForm, useAuth } from 'src/utils';
import '../profile.css';
import { Box, Button, Typography, Link, TextFieldInput, Loader, Divider, Stack } from 'src/components';
import { ProfileViewHeader, GoogleAuth, AppleAuth } from '../index';
import { useIsMobile } from 'src/hooks';

export default function Register({ handleCurrentViewChange, handleSuccess }) {
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});

    const timer = useRef();
    const isMobile = useIsMobile();
    const { loginUser } = useAuth();

    useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setFieldErrors({});

        document.activeElement.blur();

        const data = e.target;
        const { errors, isValid } = validateRegisterForm(data.name.value, data.email.value, data.password.value);
        if (!isValid) {
            setFieldErrors(errors);
            setLoading(false);
            return;
        }

        timer.current = window.setTimeout(async () => {
            try {
                const res = await register(data.name.value, data.email.value, data.password.value);
                loginUser(res.token, res.refreshToken); 
                handleSuccess("Registration successful!");
                handleCurrentViewChange("AccountSummary");
            } catch (err) {
                setErrorMsg(err.message || 'Registration failed. Please contact support');
            } finally {
                setLoading(false);
            }
        }, 1000); 
    };

    return (
        <div>
            <ProfileViewHeader title="Create Account" subtitle="Sign up to get full access" handleCurrentViewChange={handleCurrentViewChange} backTo="NotLoggedIn" alternateActionText="Already have an account?" alternateActionPage="Login" />

            <Box component="form" noValidate onSubmit={handleRegister} sx={{ mb: 1 }}>

                <TextFieldInput 
                    id="name" 
                    label="Name" 
                    type="text"
                    error={!!fieldErrors.name}
                    helperText={fieldErrors.name}/>

                <TextFieldInput 
                    id="email" 
                    label="Email Address" 
                    type="email" 
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email}/>

                <TextFieldInput 
                    id="password" 
                    label="Password" 
                    type="password"
                    error={!!fieldErrors.password}
                    helperText={fieldErrors.password}/>

                <div className="form-break  ts-and-cs">
                    <span className="link-container">
                        <Typography variant={isMobile ? "body2" : "body3"}> 
                            By signing up I agree to the PanAmApp&nbsp;<Link className="link" variant={isMobile ? "body2" : "body3"} target="_blank" href="/terms-of-service">Terms of Service</Link>
                            &nbsp;and&nbsp;<Link className="link" variant={isMobile ? "body2" : "body3"} target="_blank" href="/privacy-policy">Privacy Policy</Link>
                        </Typography>                      
                    </span>
                </div>

                {errorMsg && (
                    <Typography variant="body3" color="error">{errorMsg}</Typography>
                )}

                <Stack spacing={isMobile ? 2 : 1} mt={isMobile ? 3 : 0}>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{marginTop: '8px'}}
                        className="submit-button button"
                        disabled={loading}>
                            Sign up
                    </Button>

                    <Divider /> 
                    
                    <GoogleAuth setLoading={setLoading} handleCurrentViewChange={handleCurrentViewChange} handleSuccess={handleSuccess} />

                    <AppleAuth setLoading={setLoading} handleCurrentViewChange={handleCurrentViewChange} handleSuccess={handleSuccess} />
                    
                </Stack>

            </Box>

            {loading && (<Loader />)}

        </div>
    );
}
